import { MantineColorsTuple, rem } from '@mantine/core';
import ShadeGeneratorLib, { type IShadeGenerator, type Shade } from 'shade-generator';

// @ts-expect-error shade-generator may export as ESM default — handle both cases
const ShadeGenerator: IShadeGenerator = ShadeGeneratorLib.default || ShadeGeneratorLib;

export const breakpoints = {
  sm: '40em',
  md: '48em',
  lg: '64em',
  xl: '80em',
  '2xl': '96em',
};

const agentoDarkBlue: MantineColorsTuple = [
  '#E8F0FF', // 0 - lightest (more blue)
  '#D1E1FF', // 1
  '#B9D2FF', // 2
  '#A2C3FF', // 3
  '#5B7FE6', // 4
  '#3459CC', // 5
  '#131199', // 6 - base color
  '#101080', // 7
  '#0D0D66', // 8
  '#0A0A4D', // 9 - darkest
];

const agentoBlue: MantineColorsTuple = [
  '#E8F0FF', // 0 - lightest (more blue)
  '#D1E1FF', // 1
  '#B9D2FF', // 2
  '#A2C3FF', // 3
  '#5B7FE6', // 4
  '#3459CC', // 5
  '#1E1EC2', // 6 - base color
  '#1B1BAF', // 7
  '#18189B', // 8
  '#151588', // 9 - darkest
];

const agentoGreen: MantineColorsTuple = [
  '#e6fcf5',
  '#c3fae8',
  '#96f2d7',
  '#63e6be',
  '#38d9a9',
  '#20c997',
  '#12b886',
  '#0ca678',
  '#099268',
  '#087f5b',
];

// All colors created here need to be added on tailwind.config.ts
export const colors: Record<string, MantineColorsTuple> = {
  primary: agentoBlue, // Primary color (used for main elements like buttons)
  secondary: agentoDarkBlue, // Secondary color (used for accents or highlights)
  accent: createColorArray('#20ceff'), // Accent color (used for highlights or subtle details)
  green: agentoGreen, // Success color (used for success messages or states)
  yellow: createColorArray('#F59E0B'), // Warning color (used for warnings or alerts)
  blue: agentoBlue, // Info color (used for informational messages)
  red: createColorArray('#EF4444'), // Error color (used for error messages or states)
  dark: createColorArray('#02010a'), // Dark text color (used for main text or dark backgrounds)
  light: [
    'rgba(255, 255, 255, 0.05)', // 0 - lightest (more transparent)
    'rgba(255, 255, 255, 0.1)', // 1
    'rgba(255, 255, 255, 0.2)', // 2
    'rgba(255, 255, 255, 0.4)', // 3
    'rgba(255, 255, 255, 0.6)', // 4
    'rgba(255, 255, 255, 0.8)', // 5
    'rgba(255, 255, 255, 1)', // 6 - white (base color)
    'rgba(255, 255, 255, 1)', // 7
    'rgba(255, 255, 255, 1)', // 8
    'rgba(255, 255, 255, 1)', // 9 - darkest (more opaque)
  ],
};

export const spacings = {
  xs: rem(4),
  sm: rem(8),
  md: rem(16),
  lg: rem(24),
  xl: rem(32),
};

export const fontSize = {
  xs: rem(12),
  sm: rem(14),
  md: rem(16),
  lg: rem(18),
  xl: rem(20),
};

// Function to create an array of 10 different shades of a color
function createColorArray(color: string) {
  const config: Record<Shade, number> = {
    '10': 0,
    '20': 0,
    '30': 0,
    '40': 0.9,
    '50': 0.75,
    '60': 0.6,
    '70': 0.45,
    '80': 0.3,
    '90': 0.15,
    '100': 0,
    '200': 0.9,
    '300': 0.8,
    '400': 0.7,
    '500': 0,
    '600': 0,
    '700': 0,
    '800': 0,
    '900': 0,
    '1000': 0,
  };

  const colorMap = ShadeGenerator.hue(color).config(config).shadesMap('hex');
  const colors = [
    colorMap['40'],
    colorMap['50'],
    colorMap['60'],
    colorMap['70'],
    colorMap['80'],
    colorMap['90'],
    colorMap['100'],
    colorMap['200'],
    colorMap['300'],
    colorMap['400'],
  ] as const;

  return colors;
}
